import { getApps, initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getDatabase,
  connectDatabaseEmulator,
  ref,
  update,
  onDisconnect,
  onValue,
  off,
  push,
  serverTimestamp,
  type Database,
  type DataSnapshot
} from 'firebase/database';
import {
  getAuth,
  initializeAuth,
  inMemoryPersistence,
  signInWithCustomToken,
  signOut,
  type Auth
} from 'firebase/auth';
import { postWithAuth } from '@services/http.service';
import { getAuth as getStoredAuth, isRealUser } from '@services/identity.service';
import { RTDB_TOKEN_URL } from '@services/url.service';
import { getOrCreateDeviceId } from './device';
import { getDeviceMeta } from './deviceMeta';
import { mergeExtensionReaders } from './presence';
import { stripUndefined } from './sanitize';

// Web-app side of the Firebase RTDB control plane (see refs/realtime-sync). RTDB carries
// control/state only; everything is scoped to `/sync/{ownerId}` where ownerId is resolved
// server-side by POST /realtime-sync/token and enforced by RTDB security rules.

export interface LivePage {
  url: string;
  platform: string;
  livestreamId: string | null;
  state: 'open' | 'connected';
  // Whether the extension can currently read this open page's chat (reported by
  // the pre-connect readability probe). Present only for un-connected (`open`)
  // pages; once connected, readability rides the session's `chatReadable`.
  // Undefined = not yet reported (treat as not-readable for gating).
  chatReadable?: boolean;
}

export interface ExtensionPresence {
  installed: boolean;
  online: boolean;
  pages: Record<string, LivePage>;
}

// Every chat *reader* registers a presence node under `/sync/{ownerId}/readers/{readerId}`
// with a `type`: the browser extension today, meeting bots and server-side API pollers
// next. They share one collection so the app can see all live sources in one place.
// `installed`/`version`/`pages` are extension-specific and absent for other reader types.
export type ReaderType = 'extension' | 'bot' | 'api' | 'stream';

export interface ReaderPresence extends Partial<ExtensionPresence> {
  type?: ReaderType;
  version?: string | null;
  lastSeen?: number;
}

// Where a presentation that drives reading is running. Control-plane only.
export type RuntimeEnv = 'app' | 'embed' | 'powerpoint';

// --- Phase 2: sessions + command channel -----------------------------------

export type ReadingStatus = 'reading' | 'stopped';

// Per-livestream session state, published by the extension under
// `/sync/{ownerId}/sessions/{livestreamId}` in response to commands.
export interface SessionState {
  livestreamId: string;
  status: ReadingStatus;
  platform?: string;
  readerId?: string; // reader (extension device / bot / poller) currently handling the session
  readerType?: ReaderType;
  // Where the presentation that triggered reading runs (set from the command).
  runtimeEnv?: RuntimeEnv;
  // For iframe embeds, a label for the embedding platform: the product name when
  // recognised (e.g. 'Gamma'), otherwise the bare host domain — so the extension
  // popup can name where the presentation is running.
  embedHost?: string;
  // The interaction currently live in the session — id only, never content.
  activeInteractionId?: string;
  // The live-page URL the extension recorded at startReading (used to match a
  // session to its live tab, e.g. when overriding the reader for the same page).
  url?: string;
  startedAt?: number;
  updatedAt?: number;
  // Whether the extension can currently see the platform's chat container. When
  // false, chat is not being read (e.g. the chat panel is closed or the page
  // layout changed) and the app/embed should warn the user. Undefined = unknown.
  chatReadable?: boolean;
}

export type CommandType = 'startReading' | 'stopReading';

// A command the app/embed pushes onto `/sync/{ownerId}/commands` for the
// extension to act on. `url`/`platform` help the extension locate the live tab
// when the caller (e.g. an embed) only knows the livestream, not the tab.
export interface SyncCommandInput {
  type: CommandType;
  livestreamId: string;
  url?: string;
  platform?: string;
  // Optional hints the reader mirrors onto the session it publishes.
  runtimeEnv?: RuntimeEnv;
  embedHost?: string;
  activeInteractionId?: string;
}

// --- Isolated Firebase app for the RTDB control plane ----------------------
//
// The control plane signs in as the *team owner* via a custom token. That
// identity must never touch the app's primary Firebase auth — the signed-in
// user that Firestore reads/writes depend on. Sharing the default `auth` here
// silently swaps the user out from under Firestore (reads then fail with
// permission-denied and the Listen channel closes). So we run on a dedicated
// secondary app with its own in-memory auth and RTDB handle, exactly like the
// SharedWorker (rtdb.worker.ts).
const RTDB_APP_NAME = 'sa-rtdb-sync';
const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_BUCKET,
  databaseURL: process.env.NEXT_PUBLIC_FIREBASE_DATABASE_URL
};
const rtdbApp: FirebaseApp =
  getApps().find((a) => a.name === RTDB_APP_NAME) ??
  initializeApp(firebaseConfig, RTDB_APP_NAME);
// initializeAuth throws if the app already has an auth instance (e.g. HMR
// re-evaluates this module); fall back to the existing one.
let rtdbAuth: Auth;
try {
  rtdbAuth = initializeAuth(rtdbApp, { persistence: inMemoryPersistence });
} catch {
  rtdbAuth = getAuth(rtdbApp);
}
// Exported so any control-plane feature that already resolves its own owner can
// write through the same isolated app/socket instead of opening a second one.
export const rtdbDb: Database = getDatabase(rtdbApp);

// Point this RTDB instance at the local emulator when configured (dev only), so
// the embed reads the same control plane the extension writes to. Auth stays on
// cloud — the emulator accepts the cloud-minted custom token. Wrapped in try/catch
// because connectDatabaseEmulator throws if HMR re-evaluates this module after the
// instance is already in use.
const rtdbEmulatorHost = process.env.NEXT_PUBLIC_FIREBASE_DATABASE_EMULATOR_HOST;
if (rtdbEmulatorHost) {
  const [host, port] = rtdbEmulatorHost.split(':');
  try {
    connectDatabaseEmulator(rtdbDb, host, Number(port) || 9000);
  } catch {
    // already connected (HMR) — ignore
  }
}

// Only a real signed-in user (copilots included, guests excluded) may initiate
// the RTDB sign-in. TeamsGate mounts the realtime-sync transport around the whole
// app — login page included — and the SharedWorker re-asks each frame for a token
// every few seconds until one is supplied. Without this gate, a guest / logged-out
// session answers each ask with POST /realtime-sync/token, which fails ("No Token
// provided") and loops forever. Bailing here (no HTTP call) stops that at the source.
const canFetchRtdbToken = (): boolean => isRealUser(getStoredAuth());

let cachedOwnerId: string | null = null;
let signInPromise: Promise<string | null> | null = null;
// Bumped whenever the expected owner changes, so an in-flight sign-in for the
// previous owner can't repopulate the cache after it was invalidated.
let authEpoch = 0;
let expectedOwnerId: string | null = null;

// Announce which owner this realm should be signed in as (the teams store's
// owner key, set by the transport layer on start). When it changes while we're
// cached as a *different* owner — a team switch — tear the sign-in down so the
// next operation re-fetches a token with the current auth headers and lands
// under the new `/sync/{ownerId}`. Invalidation happens only here, once per
// change (never inside ensureSignedIn), so an owner key whose format never
// string-matches the token's ownerId costs one redundant re-auth, not a loop.
export const setExpectedOwner = (ownerKey: string | null): void => {
  const next = ownerKey ? String(ownerKey) : null;
  if (next === expectedOwnerId) return;
  expectedOwnerId = next;
  if (next && cachedOwnerId && cachedOwnerId !== next) {
    authEpoch += 1;
    cachedOwnerId = null;
    signOut(rtdbAuth).catch(() => {});
  }
};

// Fetch a custom token (scoped to the team owner) and sign the SDK in once.
// Returns the resolved ownerId, or null when not authenticated.
export const ensureSignedIn = async (): Promise<string | null> => {
  if (cachedOwnerId) return cachedOwnerId;
  if (!canFetchRtdbToken()) return null;
  if (signInPromise) return await signInPromise;

  signInPromise = (async () => {
    const epoch = authEpoch;
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res: any = await postWithAuth(RTDB_TOKEN_URL(), {});
      const token = res?.entity?.token;
      const ownerId = res?.entity?.ownerId;
      if (!token || !ownerId) return null;
      await signInWithCustomToken(rtdbAuth, token);
      if (epoch !== authEpoch) {
        // Owner switched while this sign-in was in flight — we just signed in as
        // the stale owner. Undo it; the caller's next operation re-auths fresh.
        signOut(rtdbAuth).catch(() => {});
        return null;
      }
      cachedOwnerId = ownerId;
      return ownerId;
    } catch {
      return null;
    }
  })();

  try {
    return await signInPromise;
  } finally {
    signInPromise = null;
  }
};

// Authenticated RTDB handle for features outside the control plane (Screenshot-Popup,
// FantasticFans, RunOfShow slide-status) whose paths (`screenshots/*`, `slides/*`) now
// require the signed-in rtdb-sync identity. They ride this secondary app instead of the
// default-app `realTimeDb` because the default app must never sign in (see header note).
// Resolves null for guests / logged-out sessions — callers should skip their subscription.
export const getAuthedRtdb = async (): Promise<Database | null> => {
  const ownerId = await ensureSignedIn();
  return ownerId ? rtdbDb : null;
};

// Fetch a custom token + ownerId without signing this realm in. Used to hand
// credentials to the SharedWorker (rtdb.worker.ts), which holds the connection
// and signs itself in; the main thread never opens its own socket in that path.
export const fetchRtdbToken = async (): Promise<{ token: string; ownerId: string } | null> => {
  if (!canFetchRtdbToken()) return null;
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const res: any = await postWithAuth(RTDB_TOKEN_URL(), {});
    const token = res?.entity?.token;
    const ownerId = res?.entity?.ownerId;
    if (!token || !ownerId) return null;
    return { token, ownerId };
  } catch {
    return null;
  }
};

// Publish this app instance's presence under `/sync/{ownerId}/app/{userId}/{deviceId}`.
export const publishAppPresence = async (
  userId: string,
  deviceId: string,
  context: string = 'web'
): Promise<void> => {
  const ownerId = await ensureSignedIn();
  if (!ownerId || !userId) return;

  const presenceRef = ref(rtdbDb, `sync/${ownerId}/app/${userId}/${deviceId}`);
  await onDisconnect(presenceRef).update({ online: false, lastSeen: serverTimestamp() });
  await update(presenceRef, {
    online: true, context, ...getDeviceMeta(), lastSeen: serverTimestamp()
  });
};

// Bot presence (the `type:'bot'` reader model) is not written from the app at all: the
// bot runs on its own origin and owns that path there, including its own custom-token
// sign-in for headless runtimes (apps/zoom-sdk/src/services/{botReader,rtdb}.ts). A
// future server-dispatched bot (Attendee.dev) follows the same split.

// Subscribe to the extension presence/live-pages for the current owner. Readers are
// merged: installed/online is true if any extension reader reports it, pages are
// unioned; non-extension readers (bots, pollers) are ignored here. Returns an
// unsubscribe function.
export const subscribeExtensionPresence = (
  onChange: (presence: ExtensionPresence) => void
): (() => void) => {
  let detach: (() => void) | null = null;
  let cancelled = false;

  ensureSignedIn().then((ownerId) => {
    if (!ownerId || cancelled) return;
    const readersRef = ref(rtdbDb, `sync/${ownerId}/readers`);
    const handler = (snap: DataSnapshot) => {
      const readers = (snap.val() || {}) as Record<string, ReaderPresence>;
      onChange(mergeExtensionReaders(readers));
    };
    onValue(readersRef, handler);
    detach = () => off(readersRef, 'value', handler);
  });

  return () => {
    cancelled = true;
    if (detach) detach();
  };
};

// Subscribe to live session state for the current owner. Returns an unsubscribe
// function. Emits `{}` until the SDK is signed in / the node exists.
export const subscribeSessions = (
  onChange: (sessions: Record<string, SessionState>) => void
): (() => void) => {
  let detach: (() => void) | null = null;
  let cancelled = false;

  ensureSignedIn().then((ownerId) => {
    if (!ownerId || cancelled) return;
    const sessionsRef = ref(rtdbDb, `sync/${ownerId}/sessions`);
    const handler = (snap: DataSnapshot) => {
      onChange((snap.val() || {}) as Record<string, SessionState>);
    };
    onValue(sessionsRef, handler);
    detach = () => off(sessionsRef, 'value', handler);
  });

  return () => {
    cancelled = true;
    if (detach) detach();
  };
};

// Push a command onto the owner's command queue. The extension consumes new
// commands from `/sync/{ownerId}/commands`, acts on them, then removes them.
// Resolves once the command is written (not when the extension has acted).
export const sendCommand = async (input: SyncCommandInput): Promise<void> => {
  const ownerId = await ensureSignedIn();
  if (!ownerId || !input?.livestreamId) return;
  const commandsRef = ref(rtdbDb, `sync/${ownerId}/commands`);
  // RTDB rejects any write containing `undefined` (it throws synchronously), so
  // drop unset optional fields (e.g. embedHost for non-embed runtimes) first.
  await push(commandsRef, stripUndefined({
    ...input,
    issuedBy: getOrCreateDeviceId(),
    issuedAt: serverTimestamp()
  }));
};
